/**
 * WalletContext — React context providing wallet state and actions.
 *
 * Features:
 * - Discriminated union state (uninitialized | locked | unlocking | unlocked | creating | importing | error)
 * - Auto-lock after 5 minutes of inactivity
 * - Balance refresh on interval
 * - Chain switching
 */

import { createContext, useContext, useReducer, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { WalletService, WalletServiceError, getWalletService } from './walletService.js';
import { hasVault } from './storage.js';
import type { WalletStatus, TokenBalance, WalletContextValue } from './types.js';

const AUTO_LOCK_MS = 5 * 60 * 1000; // 5 minutes
const BALANCE_REFRESH_MS = 15_000; // 15 seconds

// ─── State + Actions ──────────────────────────────────────────────────────────

interface WalletState {
  status: WalletStatus;
  nativeBalance: bigint;
  tokenBalances: TokenBalance[];
  lastBalanceUpdate: number;
  chainId: string;
}

type WalletAction =
  | { type: 'SET_STATUS'; status: WalletStatus }
  | { type: 'SET_BALANCES'; native: bigint; tokens: TokenBalance[] }
  | { type: 'SET_CHAIN'; chainId: string }
  | { type: 'LOCK' };

function reducer(state: WalletState, action: WalletAction): WalletState {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.status };
    case 'SET_BALANCES':
      return {
        ...state,
        nativeBalance: action.native,
        tokenBalances: action.tokens,
        lastBalanceUpdate: Date.now(),
      };
    case 'SET_CHAIN':
      return { ...state, chainId: action.chainId };
    case 'LOCK':
      return {
        ...state,
        status: { state: 'locked' },
        nativeBalance: 0n,
        tokenBalances: [],
      };
    default:
      return state;
  }
}

const initialState: WalletState = {
  status: { state: 'uninitialized' },
  nativeBalance: 0n,
  tokenBalances: [],
  lastBalanceUpdate: 0,
  chainId: 'eip155:56',
};

// ─── Context ──────────────────────────────────────────────────────────────────

const WalletContext = createContext<WalletContextValue | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const serviceRef = useRef<WalletService>(getWalletService(state.chainId));
  const activityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const balanceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Initialize on mount ────────────────────────────────────────────────

  useEffect(() => {
    void (async () => {
      const initialized = await hasVault();
      if (initialized) {
        dispatch({ type: 'SET_STATUS', status: { state: 'locked' } });
      } else {
        dispatch({ type: 'SET_STATUS', status: { state: 'uninitialized' } });
      }
    })();
  }, []);

  // ─── Auto-lock on inactivity ────────────────────────────────────────────

  const resetActivityTimer = useCallback(() => {
    if (state.status.state !== 'unlocked') return;

    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }
    activityTimerRef.current = setTimeout(() => {
      serviceRef.current.lock();
      dispatch({ type: 'LOCK' });
    }, AUTO_LOCK_MS);
  }, [state.status.state]);

  useEffect(() => {
    if (state.status.state !== 'unlocked') return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const handler = () => resetActivityTimer();

    events.forEach(e => document.addEventListener(e, handler, { passive: true }));
    resetActivityTimer();

    return () => {
      events.forEach(e => document.removeEventListener(e, handler));
      if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
    };
  }, [state.status.state, resetActivityTimer]);

  // ─── Balance polling ────────────────────────────────────────────────────

  const refreshBalances = useCallback(async () => {
    if (!serviceRef.current.isUnlocked()) return;
    try {
      const [native, tokens] = await Promise.all([
        serviceRef.current.getNativeBalance(),
        serviceRef.current.getTokenBalances(),
      ]);
      dispatch({ type: 'SET_BALANCES', native, tokens });
    } catch {
      // Network error — keep previous balances
    }
  }, []);

  useEffect(() => {
    if (state.status.state !== 'unlocked') {
      if (balanceTimerRef.current) {
        clearInterval(balanceTimerRef.current);
        balanceTimerRef.current = null;
      }
      return;
    }

    void refreshBalances();
    balanceTimerRef.current = setInterval(() => {
      void refreshBalances();
    }, BALANCE_REFRESH_MS);

    return () => {
      if (balanceTimerRef.current) {
        clearInterval(balanceTimerRef.current);
        balanceTimerRef.current = null;
      }
    };
  }, [state.status.state, refreshBalances]);

  // ─── Actions ────────────────────────────────────────────────────────────

  const actions = {
    async createWallet(password: string) {
      dispatch({ type: 'SET_STATUS', status: { state: 'creating' } });
      try {
        const result = await serviceRef.current.create(password);
        dispatch({
          type: 'SET_STATUS',
          status: { state: 'unlocked', address: result.address, chainId: state.chainId },
        });
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create wallet';
        dispatch({ type: 'SET_STATUS', status: { state: 'error', message } });
        throw err;
      }
    },

    async importWallet(mnemonic: string, password: string) {
      dispatch({ type: 'SET_STATUS', status: { state: 'importing' } });
      try {
        const result = await serviceRef.current.import(mnemonic, password);
        dispatch({
          type: 'SET_STATUS',
          status: { state: 'unlocked', address: result.address, chainId: state.chainId },
        });
        return result;
      } catch (err) {
        const message = err instanceof WalletServiceError ? err.message : 'Failed to import wallet';
        dispatch({ type: 'SET_STATUS', status: { state: 'error', message } });
        throw err;
      }
    },

    async unlock(password: string) {
      dispatch({ type: 'SET_STATUS', status: { state: 'unlocking' } });
      try {
        const address = await serviceRef.current.unlock(password);
        dispatch({
          type: 'SET_STATUS',
          status: { state: 'unlocked', address, chainId: state.chainId },
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to unlock wallet';
        dispatch({ type: 'SET_STATUS', status: { state: 'error', message } });
        throw err;
      }
    },

    lock() {
      serviceRef.current.lock();
      dispatch({ type: 'LOCK' });
    },

    getAddress() {
      return serviceRef.current.getAddress();
    },

    async getBalance() {
      return serviceRef.current.getNativeBalance();
    },

    async getTokenBalances() {
      return serviceRef.current.getTokenBalances();
    },

    async sendTransaction(to: string, amount: bigint, tokenAddress?: string) {
      if (tokenAddress) {
        return serviceRef.current.sendToken(tokenAddress, to, amount);
      }
      return serviceRef.current.sendNative(to, amount);
    },

    async signMessage(message: string) {
      return serviceRef.current.signMessage(message);
    },

    async signTypedData(_data: unknown) {
      // EIP-712 typed data signing — implemented when needed for channel states
      throw new Error('signTypedData not yet implemented');
    },

    getPrivateKey() {
      return serviceRef.current.getPrivateKey();
    },

    switchChain(chainId: string) {
      serviceRef.current.switchChain(chainId);
      dispatch({ type: 'SET_CHAIN', chainId });
    },
  };

  const contextValue: WalletContextValue = {
    status: state.status,
    balances: {
      native: state.nativeBalance,
      tokens: state.tokenBalances,
      lastUpdated: state.lastBalanceUpdate,
    },
    actions,
    chainId: state.chainId,
    refreshBalances,
  };

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet(): WalletContextValue {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
